import type {RequestType} from '@src/types/onyx/Request';
import {
  READ_COMMANDS,
  SIDE_EFFECT_REQUEST_COMMANDS,
  WRITE_COMMANDS,
} from './types';
import type {
  ReadCommand,
  SideEffectRequestCommand,
  WriteCommand,
} from './types';

/**
 * kiroku-api is a per-route REST surface (e.g. `GET /v1/app/open`,
 * `POST /v1/friends/request`), unlike the legacy Expensify-style single endpoint
 * (`{root}api/{Command}`). This map translates a command name to its
 * `{method, path}` on kiroku-api. The map covers every declared command, so a
 * command added without a route fails to typecheck. The legacy endpoint is gone.
 */
type KirokuRoute = {
  /** HTTP method to use against kiroku-api. */
  method: RequestType;
  /** Path under the kiroku-api root, including the `/v1` prefix. */
  path: string;
  /** Optional builder for a GET route's query params, derived from request data. */
  toQuery?: (data: Record<string, unknown>) => Record<string, string | number>;
  /**
   * Optional builder for a path with URL params (e.g. `/v1/feedback/:id/remove`),
   * derived from request data. Overrides `path` when present.
   */
  toPath?: (data: Record<string, unknown>) => string;
  /**
   * When `false`, this is a public route: `kirokuXhr` does not require a Firebase
   * ID token and skips the `Bearer` header when none is available (it still
   * attaches one opportunistically if the caller happens to be signed in).
   * Omitted/`true` means the route is authenticated, like every other route.
   */
  requiresAuth?: boolean;
};

type ApiCommand = WriteCommand | ReadCommand | SideEffectRequestCommand;

const KIROKU_ROUTES: Record<ApiCommand, KirokuRoute> = {
  [WRITE_COMMANDS.OPEN_APP]: {
    method: 'get',
    path: '/v1/app/open',
  },
  // Public signup gate: queried BEFORE a Firebase account/ID token exists, so it
  // must run without authentication. See `actions/User.signUp`.
  [SIDE_EFFECT_REQUEST_COMMANDS.GET_MIN_VERSION]: {
    method: 'get',
    path: '/v1/app/min-version',
    requiresAuth: false,
  },
  // Reconnect catch-up. With `updateIDFrom` (the client's last applied update)
  // the server replays just the missed updates from its update log when the
  // gap is small, and answers with the full OpenApp payload otherwise. Without
  // it, this is a full re-hydration like OpenApp.
  [WRITE_COMMANDS.RECONNECT_APP]: {
    method: 'get',
    path: '/v1/app/open',
    toQuery: data => {
      const query: Record<string, number> = {};
      if (typeof data.updateIDFrom === 'number' && data.updateIDFrom > 0) {
        query.updateIDFrom = data.updateIDFrom;
      }
      return query;
    },
  },
  // Friend drinking sessions, windowed by start_time and privacy-enforced
  // server-side (friends + visibility). Denied reads return 200 with an
  // eviction patch (cachedDrinkingSessions[uid] -> null), see Kiroku #786.
  [READ_COMMANDS.OPEN_FRIEND_DRINKING_SESSIONS]: {
    method: 'get',
    path: '/v1/users/:uid/sessions',
    toPath: data =>
      `/v1/users/${encodeURIComponent(String(data.userID))}/sessions`,
    toQuery: data => ({from: Number(data.from ?? 0)}),
  },
  // Public profile read (profile + public_data + public is_supporter flag). Any
  // authenticated user may read it (mirrors the rules' `users/$uid/profile`).
  [READ_COMMANDS.OPEN_PUBLIC_PROFILE_PAGE]: {
    method: 'get',
    path: '/v1/users/:uid/profile',
    toPath: data =>
      `/v1/users/${encodeURIComponent(String(data.userID))}/profile`,
  },
  // Friend rendering preferences (units/colors), privacy-enforced server-side
  // (friends + visibility). Denied reads evict userDataList[uid].preferences.
  [READ_COMMANDS.OPEN_FRIEND_PREFERENCES]: {
    method: 'get',
    path: '/v1/users/:uid/preferences',
    toPath: data =>
      `/v1/users/${encodeURIComponent(String(data.userID))}/preferences`,
  },
  // Friend presence + latest session, privacy-enforced (friends + visibility).
  // Denied reads evict userDataList[uid].user_status.
  [READ_COMMANDS.OPEN_FRIEND_STATUS]: {
    method: 'get',
    path: '/v1/users/:uid/status',
    toPath: data =>
      `/v1/users/${encodeURIComponent(String(data.userID))}/status`,
  },
  // Batched cross-user read: one request for up to 100 uids (callers chunk
  // above that), replacing the per-uid profile/status fan-out that tripped the
  // per-uid rate limiter. `fields` selects the projections (`profile` is public;
  // `status` is privacy-gated and simply absent for a denied/hidden user). The
  // server merges `userDataList[uid].{profile,user_status,...}` per uid, same as
  // the single-user reads. `uids` is comma-joined here and percent-encoded by
  // the HTTP layer (matches the SEARCH_USERS style — no pre-encoding).
  [READ_COMMANDS.GET_USERS_BATCH]: {
    method: 'get',
    path: '/v1/users/batch',
    toQuery: data => ({
      uids: Array.isArray(data.userIDs) ? data.userIDs.join(',') : '',
      fields: typeof data.fields === 'string' ? data.fields : 'profile,status',
    }),
  },
  // A user's friends list (public, like the profile read). Backs ProfileScreen's
  // friend / common-friend counts; merged into userDataList[uid].friends.
  [READ_COMMANDS.OPEN_FRIEND_LIST]: {
    method: 'get',
    path: '/v1/users/:uid/friends',
    toPath: data =>
      `/v1/users/${encodeURIComponent(String(data.userID))}/friends`,
  },
  [SIDE_EFFECT_REQUEST_COMMANDS.GET_MISSING_ONYX_MESSAGES]: {
    method: 'get',
    path: '/v1/updates',
    toQuery: data => ({
      from: Number(data.updateIDFrom ?? 0),
      to: Number(data.updateIDTo ?? 0),
    }),
  },
  // Friend prefix search. The server tokenizes `q` and prefix-matches the
  // `nickname_to_id` index; matches ride back in the response's `searchResults`.
  [READ_COMMANDS.SEARCH_USERS]: {
    method: 'get',
    path: '/v1/users/search',
    toQuery: data => ({q: typeof data.q === 'string' ? data.q : ''}),
  },
  [WRITE_COMMANDS.PROVISION_USER]: {
    method: 'post',
    path: '/v1/provisioning',
  },
  [WRITE_COMMANDS.CLOSE_ACCOUNT]: {
    method: 'post',
    path: '/v1/account/close',
  },
  // Push device registry. Register on sign-in, app start, and FCM token
  // refresh; unregister on sign-out (see actions/PushNotification).
  [WRITE_COMMANDS.REGISTER_PUSH_DEVICE]: {
    method: 'post',
    path: '/v1/notifications/devices',
  },
  [WRITE_COMMANDS.UNREGISTER_PUSH_DEVICE]: {
    method: 'delete',
    path: '/v1/notifications/devices/:deviceID',
    toPath: data =>
      `/v1/notifications/devices/${encodeURIComponent(String(data.deviceID))}`,
  },
  [WRITE_COMMANDS.SEND_FRIEND_REQUEST]: {
    method: 'post',
    path: '/v1/friends/request',
  },
  // Personal invite links. The caller's own code comes back as
  // `merge userDataList { [uid]: { invite_code } }` in onyxData.
  [SIDE_EFFECT_REQUEST_COMMANDS.GET_INVITE_CODE]: {
    method: 'get',
    path: '/v1/friends/invite',
  },
  [SIDE_EFFECT_REQUEST_COMMANDS.RESET_INVITE_CODE]: {
    method: 'post',
    path: '/v1/friends/invite/reset',
  },
  // Public preview of an invite link's owner (`invitePreview` sidecar). Used
  // signed out too (web visitors, native before sign-in), so it runs without a
  // token. The server answers it before its auth gate, so it never returns the
  // 401 that would trigger the revoked-token sign-out for a signed-in caller.
  [SIDE_EFFECT_REQUEST_COMMANDS.GET_INVITE_PREVIEW]: {
    method: 'get',
    path: '/v1/friends/invite/:code',
    toPath: data =>
      `/v1/friends/invite/${encodeURIComponent(String(data.code))}`,
    requiresAuth: false,
  },
  [SIDE_EFFECT_REQUEST_COMMANDS.REDEEM_INVITE]: {
    method: 'post',
    path: '/v1/friends/invite/:code/redeem',
    toPath: data =>
      `/v1/friends/invite/${encodeURIComponent(String(data.code))}/redeem`,
  },
  [WRITE_COMMANDS.ACCEPT_FRIEND_REQUEST]: {
    method: 'post',
    path: '/v1/friends/accept',
  },
  [WRITE_COMMANDS.DELETE_FRIEND_REQUEST]: {
    method: 'post',
    path: '/v1/friends/delete-request',
  },
  [WRITE_COMMANDS.UNFRIEND]: {
    method: 'post',
    path: '/v1/friends/remove',
  },
  [WRITE_COMMANDS.BLOCK_USER]: {
    method: 'post',
    path: '/v1/friends/block',
  },
  [WRITE_COMMANDS.UNBLOCK_USER]: {
    method: 'post',
    path: '/v1/friends/unblock',
  },
  [WRITE_COMMANDS.REPORT_USER]: {
    method: 'post',
    path: '/v1/reports',
  },
  [WRITE_COMMANDS.UPDATE_SESSION]: {
    method: 'post',
    path: '/v1/sessions/update',
  },
  [WRITE_COMMANDS.DELETE_SESSION]: {
    method: 'post',
    path: '/v1/sessions/delete',
  },
  // Session ops (Sessions v2). The op id doubles as the Idempotency-Key.
  [WRITE_COMMANDS.SESSION_OP]: {
    method: 'post',
    path: '/v1/sessions/ops',
  },
  [WRITE_COMMANDS.UPDATE_PREFERENCES]: {
    method: 'post',
    path: '/v1/preferences',
  },
  // Timezone lives under `users/$uid` but is written through the same
  // preferences endpoint (it handles a `timezone` object in the body).
  [WRITE_COMMANDS.UPDATE_AUTOMATIC_TIMEZONE]: {
    method: 'post',
    path: '/v1/preferences',
  },
  [WRITE_COMMANDS.UPDATE_SELECTED_TIMEZONE]: {
    method: 'post',
    path: '/v1/preferences',
  },
  [WRITE_COMMANDS.CAPTURE_SESSION_LOCATION]: {
    method: 'post',
    path: '/v1/session-locations/capture',
  },
  [WRITE_COMMANDS.CLEAR_SESSION_LOCATIONS]: {
    method: 'post',
    path: '/v1/session-locations/clear',
  },
  [WRITE_COMMANDS.PURGE_SESSION_LOCATIONS]: {
    method: 'post',
    path: '/v1/session-locations/purge',
  },
  [WRITE_COMMANDS.SET_HIDE_FROM_ALL_FRIENDS]: {
    method: 'post',
    path: '/v1/privacy/hide-from-all',
  },
  [WRITE_COMMANDS.SET_FRIEND_DATA_HIDDEN]: {
    method: 'post',
    path: '/v1/privacy/friend',
  },
  [SIDE_EFFECT_REQUEST_COMMANDS.GET_FEEDBACK_LIST]: {
    method: 'get',
    path: '/v1/feedback',
  },
  [SIDE_EFFECT_REQUEST_COMMANDS.GET_BUG_LIST]: {
    method: 'get',
    path: '/v1/feedback/bug',
  },
  [WRITE_COMMANDS.SUBMIT_FEEDBACK]: {
    method: 'post',
    path: '/v1/feedback',
  },
  [WRITE_COMMANDS.REPORT_BUG]: {
    method: 'post',
    path: '/v1/feedback/bug',
  },
  [WRITE_COMMANDS.REMOVE_FEEDBACK]: {
    method: 'post',
    path: '/v1/feedback/:feedbackId/remove',
    toPath: data =>
      `/v1/feedback/${encodeURIComponent(String(data.feedbackId))}/remove`,
  },
  [WRITE_COMMANDS.REMOVE_BUG]: {
    method: 'post',
    path: '/v1/feedback/bug/:bugId/remove',
    toPath: data =>
      `/v1/feedback/bug/${encodeURIComponent(String(data.bugId))}/remove`,
  },
  [WRITE_COMMANDS.UPDATE_DISPLAY_NAME]: {
    method: 'post',
    path: '/v1/profile/display-name',
  },
  [WRITE_COMMANDS.SET_USERNAME]: {
    method: 'post',
    path: '/v1/profile/username',
  },
  [WRITE_COMMANDS.UPDATE_LEGAL_NAME]: {
    method: 'post',
    path: '/v1/profile/name',
  },
  // Mint a presigned bucket PUT URL for a direct-to-storage image upload
  // (avatar now; session images later). Returns `{uploadUrl, objectPath}`.
  // See Kiroku #1059.
  [READ_COMMANDS.GET_IMAGE_UPLOAD_URL]: {
    method: 'post',
    path: '/v1/images/upload-url',
  },
  // Validate + moderate the uploaded object, then apply its access policy and
  // persist it. For `kind: 'avatar'`: public-read + a `profile.photo_url` merge
  // in the response's onyxData. For `kind: 'session'`: the object stays private
  // and the photo is persisted onto the session, whose merge rides back in the
  // response. Supersedes the old client-trusted `UPDATE_PROFILE_PHOTO`
  // (`/v1/profile/photo`), closing that integrity gap.
  [WRITE_COMMANDS.FINALIZE_IMAGE]: {
    method: 'post',
    path: '/v1/images/finalize',
  },
  // Remove one session photo: the stored object and its record. Owner only.
  [WRITE_COMMANDS.DELETE_SESSION_PHOTO]: {
    method: 'post',
    path: '/v1/images/session-photo/delete',
  },
  // Short-lived signed READ urls for a session's photos. Session images are
  // private objects (unlike avatars, which are public-read), so this is the
  // only way to display one. `userID` is omitted for the user's own session.
  [READ_COMMANDS.GET_SESSION_PHOTOS]: {
    method: 'get',
    path: '/v1/images/session-photos',
    toQuery: data => {
      const query: Record<string, string> = {
        sessionId: String(data.sessionId),
      };
      if (typeof data.userID === 'string' && data.userID !== '') {
        query.uid = data.userID;
      }
      return query;
    },
  },
  [WRITE_COMMANDS.SYNC_USER_STATUS]: {
    method: 'post',
    path: '/v1/status/sync',
  },
  [WRITE_COMMANDS.ACCEPT_TERMS]: {
    method: 'post',
    path: '/v1/onboarding/accept-terms',
  },
  [WRITE_COMMANDS.COMPLETE_ONBOARDING]: {
    method: 'post',
    path: '/v1/onboarding/complete',
  },
  [WRITE_COMMANDS.SET_ONBOARDING_LAST_VISITED_PATH]: {
    method: 'post',
    path: '/v1/onboarding/last-visited-path',
  },
};

/**
 * kiroku-api paths that are reached by a plain `fetch` instead of the
 * `API.write/read` pipeline, but whose path strings still belong here so this
 * file remains the single source of truth for every `/v1/...` path the client
 * knows about. Always combine with `ApiUtils.getKirokuApiRoot()` for the root.
 *
 * Unlike public routes (which now live in `KIROKU_ROUTES` with
 * `requiresAuth: false`), the entry below genuinely cannot be dispatched through
 * the pipeline at all.
 */
const KIROKU_DIRECT_PATHS = {
  /**
   * Pusher channel authorization. pusher-js drives this call through its own
   * authorizer callback (not our `API.*` layer) and expects a Pusher `{auth}`
   * payload back rather than an Onyx response envelope, so it can never be a
   * pipeline command. It is also authenticated: the fetch attaches an
   * `Authorization: Bearer <ID token>` header manually. See
   * `Pusher/kirokuAuthorizer`.
   */
  PUSHER_AUTH: '/v1/pusher/auth',

  /**
   * Unauthenticated health check. Used as NetInfo's internet-reachability
   * probe target (see `UserConnectionContext`): the probe is a bare `fetch`
   * driven by the NetInfo library itself, so it can never go through the
   * pipeline.
   */
  HEALTHZ: '/v1/healthz',
} as const;

/** Returns the kiroku-api route for a command, or undefined for an unknown one. */
function getKirokuRoute(command: string): KirokuRoute | undefined {
  return (KIROKU_ROUTES as Partial<Record<string, KirokuRoute>>)[command];
}

export {getKirokuRoute, KIROKU_DIRECT_PATHS};
export type {KirokuRoute};
